import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { currencies } from "../../../utilities/RequestData";
import { addExecutiveRequest } from "../../../../contents/redux/actions/executiveRequestActions";
import { fetchPaymentPurposes } from "../../../../contents/redux/actions/paymentPurposeActions";
import {
  MonetizationOnRounded,
  PaymentsRounded,
  LiveHelpRounded,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import styles from "../../../utilities/cssmodules/ExecutiveRequest.module.css";

const ExecutiveRequest = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { requestTypeId } = location.state || {};
  const userId = localStorage.getItem("userId");
  const dispatch = useDispatch();
  const [inputs, setInputs] = useState({
    amount: "",
    currency: "",
    purpose: "",
  });
  const [isFormValid, setIsFormValid] = useState(false);

  // Redux state
  const {
    paymentPurposes,
    isLoading: purposesLoading,
    isError: purposesError,
  } = useSelector((state) => state.paymentPurposes || {});
  const { errors } = useSelector(
    (state) => state.executiveRequests || { errors: {} },
  );

  // Fetch payment purposes
  useEffect(() => {
    dispatch(fetchPaymentPurposes());
  }, [dispatch]);

  // Validate form
  useEffect(() => {
    const isValid =
      inputs.amount &&
      inputs.currency &&
      inputs.purpose &&
      paymentPurposes?.length > 0;
    setIsFormValid(isValid);
  }, [inputs, paymentPurposes]);

  // Handle input changes
  const handleChange = (event) => {
    const { name, value } = event.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  // Validate and save form
  const handleSave = (e) => {
    e.preventDefault();
    if (!isFormValid) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (!requestTypeId) {
      toast.error("Request Type ID is missing.");
      return;
    }
    if (!userId) {
      toast.error("User ID is missing. Please log in again.");
      return;
    }

    const requestData = {
      amount: parseFloat(inputs.amount),
      currency: inputs.currency,
      purpose: inputs.purpose,
      userId: parseInt(userId),
      isOnSaved: true,
      requestType: parseInt(requestTypeId),
    };

    dispatch(addExecutiveRequest(requestData))
      .then(() => {
        setInputs({ amount: "", currency: "", purpose: "" });
        toast.success("Executive request saved successfully.");
        navigate("/myrequests");
      })
      .catch((error) => {
        const errorMessage = error.response?.data?.errors
          ? Object.values(error.response.data.errors).flat().join("; ")
          : error.message;
        toast.error(`Failed to save executive request: ${errorMessage}`);
      });
  };

  // Validate and submit form
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isFormValid) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (!requestTypeId) {
      toast.error("Request Type ID is missing.");
      return;
    }
    if (!userId) {
      toast.error("User ID is missing. Please log in again.");
      return;
    }

    const requestData = {
      amount: parseFloat(inputs.amount),
      currency: inputs.currency,
      purpose: inputs.purpose,
      userId: parseInt(userId),
      isOnSaved: false,
      requestType: parseInt(requestTypeId),
    };

    dispatch(addExecutiveRequest(requestData))
      .then(() => {
        setInputs({ amount: "", currency: "", purpose: "" });
        toast.success("Executive request submitted successfully.");
        navigate("/myrequests");
      })
      .catch((error) => {
        const errorMessage = error.response?.data?.errors
          ? Object.values(error.response.data.errors).flat().join("; ")
          : error.message;
        toast.error(`Failed to submit executive request: ${errorMessage}`);
      });
  };

  return (
    <div className={styles["component-wrapper"]}>
      <div className={styles["component-title"]}>Executive Request</div>
      <div className={styles["generic-activity-component-description"]}>
        Create a new executive request by filling out the form below and
        submitting when you are done. Ensure all fields have accurate
        information.
      </div>
      {errors.addExecutiveRequest && (
        <div className={styles["error-message"]}>
          errors.addExecutiveRequest
        </div>
      )}
      {purposesError && (
        <div className={styles["error-message"]}>
          Error loading payment purposes
        </div>
      )}
      {purposesLoading && (
        <div className={styles["loading-message"]}>
          Loading payment purposes...
        </div>
      )}
      {!purposesLoading &&
        (!paymentPurposes || paymentPurposes.length === 0) && (
          <div className={styles["error-message"]}>
            No payment purposes available
          </div>
        )}
      <div className={styles["generic_request_content_component_wrapper"]}>
        <form
          className={styles["generic-activity-component-form"]}
          onSubmit={(e) => handleSubmit(e)}
        >
          <label className={styles["form-label"]}>
            <MonetizationOnRounded /> Amount
          </label>
          <section className={styles["form-section"]}>
            <input
              type="number"
              name="amount"
              placeholder="Enter amount"
              required
              value={inputs.amount}
              onChange={handleChange}
              className={styles["form-input"]}
              aria-label="Amount"
            />
          </section>

          <label className={styles["form-label"]}>
            <PaymentsRounded /> Currency
          </label>
          <section className={styles["form-section"]}>
            <select
              name="currency"
              required
              value={inputs.currency}
              onChange={handleChange}
              className={styles["form-select"]}
              aria-label="Currency"
            >
              <option value="">Select a currency</option>
              {currencies.map((currency) => (
                <option key={currency.id} value={currency.name}>
                  {currency.name}
                </option>
              ))}
            </select>
          </section>

          <label className={styles["form-label"]}>
            <LiveHelpRounded /> Purpose
          </label>
          <section className={styles["form-section"]}>
            <select
              name="purpose"
              required
              value={inputs.purpose}
              onChange={handleChange}
              className={styles["form-select"]}
              aria-label="Purpose"
            >
              <option value="">Select a purpose</option>
              {Array.isArray(paymentPurposes) &&
                paymentPurposes.map((purpose) => (
                  <option key={purpose.id} value={purpose.name}>
                    {purpose.name}
                  </option>
                ))}
            </select>
          </section>

          <div className={styles["button-row"]}>
            <button
              type="button"
              className={styles["generic-save-btn"]}
              onClick={handleSave}
              disabled={!isFormValid || purposesLoading}
              aria-label="Save executive request"
            >
              Save
            </button>
            <button
              type="button"
              className={styles["generic-submit-btn"]}
              onClick={handleSubmit}
              disabled={!isFormValid || purposesLoading}
              aria-label="Submit executive request"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExecutiveRequest;
